import React, { useRef, useEffect, useState } from 'react';
import { MessageSquareMore, Plus, Pencil, Check, X, ThumbsUp, ThumbsDown, Copy, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { LoadingImage } from '@/components/ui/LoadingImage';
import { getImageURL } from '@/utils/ERPUtils';
import { ImageEnlargementModal } from '@/components/chat/ImageEnlargementModal';
import { useStreamingMessages } from '@/hooks/useStreamingMessages';
import { useMessageCycle } from '@/hooks/useMessageCycle';
import { LogoIcon } from '@/components/icons/CustomIcons';
import { KnowledgeBaseSources } from '@/components/chat/KnowledgeBaseSources';
import { MessageContent } from '@/components/ui/MessageContent';
import { BRAND_NAME } from '@/utils/branding';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
}

interface ChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  images?: string[];
  videoUrl?: string;
  isComplete?: boolean;
  sources?: Array<{
    title?: string;
    url?: string;
    file_name?: string;
    file_type?: string;
    relevance_score?: number;
    snippet?: string;
    qdrant_id?: string;
  }>;
  citations?: Array<{
    id: string;
    name: string;
    description: string;
    url: string;
  }>;
  feedbackGiven?: 'thumbs_up' | 'thumbs_down' | null;
}

interface MobileCentralInterfaceProps {
  currentRole: string;
  activeAgent: string;
  onChatSubmit?: (message: string) => Promise<void>;
  isUserTyping?: boolean;
  isChatMode?: boolean;
  chatMessages?: ChatMessage[];
  isTyping?: boolean;
  currentUserName?: string;
  chatTitle?: string;
  chatSessionId?: string | null;
  isEditingTitle?: boolean;
  editedTitle?: string;
  onEditedTitleChange?: (value: string) => void;
  onStartEditTitle?: () => void;
  onSaveTitle?: () => void;
  onCancelEditTitle?: () => void;
  onTitleKeyPress?: (e: React.KeyboardEvent) => void;
  onNewChat?: () => void;
  isLoadingSession?: boolean;
  onFeedback?: (messageId: string, msgIdx: number, feedbackType: 'thumbs_up' | 'thumbs_down') => void;
  isKnowledgeMode?: boolean;
  onSaveToKB?: (content: string) => void;
}

export const MobileCentralInterface: React.FC<MobileCentralInterfaceProps> = ({
  currentRole,
  activeAgent,
  onChatSubmit,
  isUserTyping = false,
  isChatMode: isChatModeProp,
  chatMessages,
  isTyping: _isTypingProp, // Kept for backwards compatibility, thinking state now shown via message.isComplete
  currentUserName,
  chatTitle = '',
  chatSessionId,
  isEditingTitle = false,
  editedTitle = '',
  onEditedTitleChange,
  onStartEditTitle,
  onSaveTitle,
  onCancelEditTitle,
  onTitleKeyPress,
  onNewChat,
  isLoadingSession = false,
  onFeedback,
  isKnowledgeMode = false,
  onSaveToKB
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Image enlargement modal state
  const [enlargementModal, setEnlargementModal] = useState<{
    isOpen: boolean;
    images: string[];
    currentIndex: number;
  } | null>(null);

  // Image enlargement handlers
  const handleImageEnlarge = (images: string[], startIndex: number) => {
    // Clamp to valid range to prevent out-of-bounds
    const validIndex = Math.max(0, Math.min(startIndex, images.length - 1));
    setEnlargementModal({
      isOpen: true,
      images,
      currentIndex: validIndex,
    });
  };

  const handleEnlargementClose = () => {
    setEnlargementModal(null);
  };

  const handleEnlargementNavigate = (index: number) => {
    setEnlargementModal(prev => {
      if (!prev) return null;
      // Clamp index to valid range
      const validIndex = Math.max(0, Math.min(index, prev.images.length - 1));
      return { ...prev, currentIndex: validIndex };
    });
  };

  // Use the actual logged-in user's name from props, fallback to 'User'
  const currentName = currentUserName || 'User';

  // Use props directly for chat state (managed by parent MobileLayout)
  const isChatMode = isChatModeProp || false;
  const messages = chatMessages || [];
  // Note: isTyping prop is no longer used - thinking state is shown via message.isComplete === false

  // Streaming messages for thinking indicator
  const { getMessages, intervalMs } = useStreamingMessages();
  const generalChatMessages = getMessages('General Chat');
  const isWaitingForResponse = messages.some(msg => msg.role === 'assistant' && msg.isComplete === false);
  const thinkingMessage = useMessageCycle(isWaitingForResponse, generalChatMessages, intervalMs);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Show welcome screen when not in chat mode - PDF page 5 design
  if (!isChatMode) {
    return (
      <div
        className="flex flex-col justify-end h-full px-8 pb-24  "
      >
        {/* Welcome Message - positioned at bottom-left per PDF page 5 */}
        <div className="text-left">
          <h1
            className="text-foreground leading-none"
            style={{
              fontSize: '40px',
              fontWeight: 800,
              letterSpacing: '-0.5px',
              lineHeight: '20px'
            }}
          >
            Hello
          </h1>
          <h1
            className="text-foreground mb-1"
            style={{
              fontSize: '40px',
              fontWeight: 800,
              letterSpacing: '-0.5px',
              lineHeight: '64px'
            }}
          >
            {currentName},
          </h1>
          <p
            className="text-muted-foreground"
            style={{
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '30px'
            }}
          >
            Meet {BRAND_NAME}, your AI landscape assistant.
          </p>
        </div>
      </div>
    );
  }

  // Format timestamp
  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Chat mode interface - Clean modern design
  return (
    <div className="flex flex-col h-full bg-background">
      {/* Chat Header with Actions */}
      <div className="flex items-center justify-between px-4 py-3 bg-card border-b border-border">
        {/* Left: Title */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 bg-primary rounded-full flex-shrink-0 flex items-center justify-center shadow-sm">
            <MessageSquareMore className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex items-center gap-2 min-w-0">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editedTitle}
                  onChange={(e) => onEditedTitleChange?.(e.target.value)}
                  onKeyDown={onTitleKeyPress}
                  className="h-8 w-40 text-sm font-semibold"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSaveTitle}
                  className="h-7 w-7 p-0 text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/15"
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCancelEditTitle}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-foreground text-base truncate max-w-[200px]">{chatTitle}</h2>
                {chatSessionId && onStartEditTitle && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onStartEditTitle}
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        {/* New Chat Button */}
        {onNewChat && (
          <Button
            variant="outline"
            size="sm"
            onClick={onNewChat}
            className={`flex items-center text-foreground border-border hover:bg-accent flex-shrink-0 ${isEditingTitle ? 'h-8 w-8 p-0' : 'gap-1.5'}`}
          >
            <Plus className="h-4 w-4" />
            {!isEditingTitle && <span>New</span>}
          </Button>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full [&>div>div]:!block">
          <div className="px-4 py-6 space-y-6 pb-32 overflow-x-hidden max-w-full">
            {/* Loading skeleton while restoring session */}
            <div className={`space-y-6 transition-opacity duration-500 ease-in-out ${isLoadingSession ? 'opacity-100' : 'opacity-0 absolute pointer-events-none'}`}>
              {/* User message skeleton */}
              <div className="flex justify-end gap-2">
                <div className="max-w-[80%] space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-10 w-10 rounded-full" />
              </div>

              {/* Assistant message skeleton */}
              <div className="flex gap-2">
                <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-4/6" />
                  <Skeleton className="h-48 w-full rounded-lg mt-2" />
                </div>
              </div>

              {/* Another user message skeleton */}
              <div className="flex justify-end gap-2">
                <div className="max-w-[80%] space-y-2">
                  <Skeleton className="h-4 w-40" />
                </div>
                <Skeleton className="h-10 w-10 rounded-full" />
              </div>
            </div>

            {/* No conversation message */}
            {!isLoadingSession && messages.length === 0 && (
              <div className="flex items-center justify-center h-32 text-muted-foreground transition-opacity duration-500 ease-in-out animate-in fade-in">
                No conversation found
              </div>
            )}

            {/* Chat Messages */}
            <div className={`space-y-6 transition-opacity duration-500 ease-in-out ${isLoadingSession ? 'opacity-0' : 'opacity-100'}`}>
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`flex w-full ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {/* User message */}
                {message.role === 'user' ? (
                  <div className="flex flex-col items-end max-w-[85%] min-w-0 space-y-3">
                    {/* User uploaded images */}
                    {message.images && message.images.length > 0 && (
                      <div className="w-full space-y-2">
                        {message.images.map((imagePath, idx) => {
                          const imageUrl = getImageURL(imagePath);
                          return imageUrl ? (
                            <div key={idx} className="w-fit max-w-[85%] rounded-2xl overflow-hidden shadow-lg">
                              <LoadingImage
                                src={imageUrl}
                                alt={`Uploaded ${idx + 1}`}
                                className="max-h-[70vh] w-auto h-auto"
                              />
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                    {/* Text bubble - only show if content exists */}
                    {message.content && (
                      <div className="self-end px-4 py-3 bg-primary/15 text-foreground rounded-2xl rounded-tr-sm max-w-full">
                        <div className="text-[15px] leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">
                          {message.content}
                        </div>
                      </div>
                    )}
                    <span className="text-[10px] text-muted-foreground mr-1">
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                ) : (
                  /* Assistant message - Vibescaping structure */
                  <div className="flex flex-col w-full min-w-0 overflow-hidden space-y-3">
                    {/* Thinking indicator - shown OUTSIDE bubble when no content yet */}
                    {message.content === '' && message.isComplete === false && (
                      <div className="flex items-center gap-3 p-4">
                        <div className="relative">
                          <LogoIcon className="w-8 h-8" pulsing={true} />
                        </div>
                        <span className="text-muted-foreground flex items-center gap-2">
                          {thinkingMessage}
                          <span className="inline-flex items-center gap-1 ml-1">
                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></span>
                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></span>
                          </span>
                        </span>
                      </div>
                    )}
                    {/* Text bubble - only show if there's content */}
                    {message.content && (
                    <div className="w-full px-4 py-3 bg-secondary text-secondary-foreground rounded-2xl rounded-tl-sm overflow-x-auto overflow-y-hidden min-w-0 [overflow-wrap:anywhere]">
                      <MessageContent content={message.content} isDark={true} className="text-[15px] leading-relaxed" sources={message.sources} />

                      {/* Display citations inside bubble */}
                      {message.citations && message.citations.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-white/20">
                          <p className="text-xs font-semibold text-white/80 mb-1">Referenced Standards:</p>
                          <ul className="space-y-1">
                            {message.citations.map((citation, idx) => (
                              <li key={citation.id || idx} className="text-xs">
                                <a
                                  href={citation.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-300 hover:underline"
                                  title={citation.description}
                                >
                                  {citation.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Display Knowledge Base sources - Collapsible like desktop */}
                      {message.sources && message.sources.length > 0 && message.isComplete && (
                        <KnowledgeBaseSources sources={message.sources} />
                      )}
                    </div>
                    )}
                    {/* Action buttons for completed assistant messages */}
                    {message.isComplete && message.content && (
                      <div className="flex items-center gap-1 mt-1 px-1">
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(message.content);
                              toast.success('Copied to clipboard');
                            } catch {
                              const textarea = document.createElement('textarea');
                              textarea.value = message.content;
                              textarea.style.position = 'fixed';
                              textarea.style.opacity = '0';
                              document.body.appendChild(textarea);
                              textarea.select();
                              document.execCommand('copy');
                              document.body.removeChild(textarea);
                              toast.success('Copied to clipboard');
                            }
                          }}
                          className="p-1.5 rounded-md text-muted-foreground active:text-foreground transition-colors"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        {!isKnowledgeMode && onSaveToKB && (
                          <button
                            onClick={() => onSaveToKB(message.content)}
                            className="p-1.5 rounded-md text-muted-foreground active:text-foreground transition-colors"
                          >
                            <BookOpen className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {message.sources && message.sources.length > 0 && (
                          <>
                            <span className="w-px h-4 bg-border mx-1" />
                            <button
                              onClick={() => {
                                if (onFeedback) onFeedback(message.id, index, 'thumbs_up');
                              }}
                              className={`h-9 w-9 rounded-full border flex items-center justify-center transition-colors ${
                                message.feedbackGiven === 'thumbs_up'
                                  ? 'border-emerald-500/60 bg-emerald-500/15'
                                  : 'border-border active:bg-muted/50'
                              }`}
                            >
                              <ThumbsUp className={`w-4 h-4 ${message.feedbackGiven === 'thumbs_up' ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                            </button>
                            <button
                              onClick={() => {
                                if (onFeedback) onFeedback(message.id, index, 'thumbs_down');
                              }}
                              className={`h-9 w-9 rounded-full border flex items-center justify-center transition-colors ${
                                message.feedbackGiven === 'thumbs_down'
                                  ? 'border-red-500/60 bg-red-500/15'
                                  : 'border-border active:bg-muted/50'
                              }`}
                            >
                              <ThumbsDown className={`w-4 h-4 ${message.feedbackGiven === 'thumbs_down' ? 'text-red-400' : 'text-muted-foreground'}`} />
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Thinking indicator - shown OUTSIDE bubble when content exists but still streaming */}
                    {message.content !== '' && message.isComplete === false && (
                      <div className="flex items-center gap-3 p-4">
                        <div className="relative">
                          <LogoIcon className="w-8 h-8" pulsing={true} />
                        </div>
                        <span className="text-muted-foreground flex items-center gap-2">
                          {thinkingMessage}
                          <span className="inline-flex items-center gap-1 ml-1">
                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></span>
                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></span>
                          </span>
                        </span>
                      </div>
                    )}

                    {/* Images OUTSIDE bubble - Vibescaping style */}
                    {message.images && message.images.length > 0 && (
                      <div className="space-y-3">
                        {(() => {
                          // Build array of valid image URLs only
                          const allImageUrls = message.images!.map(p => getImageURL(p)).filter((u): u is string => u !== null);

                          return allImageUrls.map((imageUrl, validIdx) => (
                            <div
                              key={validIdx}
                              onClick={() => handleImageEnlarge(allImageUrls, validIdx)}
                              className="w-full rounded-2xl overflow-hidden relative transition-all cursor-pointer"
                              title="Tap to enlarge"
                            >
                              <LoadingImage
                                src={imageUrl}
                                alt={`Generated image ${validIdx + 1}`}
                                className="max-h-[70vh] w-auto h-auto"
                              />
                            </div>
                          ));
                        })()}
                      </div>
                    )}

                    {/* Video OUTSIDE bubble - Vibescaping style */}
                    {message.videoUrl && (
                      <div className="rounded-2xl overflow-hidden shadow-lg bg-black">
                        <video
                          src={getImageURL(message.videoUrl) || ''}
                          controls
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-auto"
                          style={{ aspectRatio: '16/9' }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    )}

                    {/* Timestamp */}
                    <span className="text-[10px] text-muted-foreground ml-1">
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                )}
              </div>
            ))}
            </div>

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Image Enlargement Modal */}
      {enlargementModal && (
        <ImageEnlargementModal
          isOpen={enlargementModal.isOpen}
          onClose={handleEnlargementClose}
          images={enlargementModal.images}
          currentIndex={enlargementModal.currentIndex}
          onNavigate={handleEnlargementNavigate}
        />
      )}
    </div>
  );
};